/**
 * SessionPicker.jsx — Customer-facing session selector + seat count
 *
 * Reads available sessions for a class card from Firestore.
 * Displays session cards with date, time, and remaining spots.
 * Customer selects a session and chooses number of seats (1–8, max = remaining).
 *
 * Props:
 *   cardId       {string}   — Firestore classCards document ID
 *   accent       {string}   — accent color for this class (hex)
 *   onSelect     {Function} — called with { session, seats } when confirmed
 */
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { db, functions } from '../firebase';
import { useSessions } from '../services/firestore';
import ClassIcon from './icons/ClassIcon';
import { TEMPLATE_TO_CLASS_ICON } from '../data/defaultTemplates';

// Convert "15:00" → "3:00 PM"
function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Format duration: 30 → "30 mins", 60 → "1 hr", 90 → "1 hr 30 mins", 120 → "2 hrs"
function formatDuration(d) {
  if (!d) return '';
  const mins = Number(d);
  if (isNaN(mins)) return d;
  if (mins < 60) return `${mins} mins`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  const hLabel = `${hrs} hr${hrs > 1 ? 's' : ''}`;
  return rem === 0 ? hLabel : `${hLabel} ${rem} mins`;
}

/* ── Waitlist Form (shown when class is sold out or has no sessions) ──── */

function WaitlistForm({ cardId, cardTitle, accent, reason = 'sold-out' }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);

  // Load waitlist form config from Firestore
  useEffect(() => {
    getDoc(doc(db, 'waitlistForms', 'default'))
      .then((snap) => {
        if (snap.exists()) setConfig(snap.data());
      })
      .catch(() => {});
  }, []);

  // Pre-warm the submitWaitlist function so the real submit is instant.
  useEffect(() => {
    httpsCallable(
      functions,
      'submitWaitlist',
    )({ warmup: true }).catch(() => {});
  }, []);

  // Resolve field config: label + enabled
  const f = (key, fallback) => {
    const field = config?.fields?.[key];
    if (!field) return { enabled: true, label: fallback };
    return {
      enabled: field.enabled !== false,
      label: field.label || fallback,
    };
  };

  const firstNameField = f('firstName', 'First Name');
  const lastNameField = f('lastName', 'Last Name');
  const emailField = f('email', 'Email');
  const phoneField = f('phone', 'Phone');

  const isSoldOut = reason === 'sold-out';

  const headerTitle = isSoldOut ? 'Sold Out' : 'Join the Waitlist';

  const headerMsg = isSoldOut
    ? config?.message ||
      'All sessions for this class are currently full. Join the waitlist and we\u2019ll let you know when a spot opens up!'
    : 'We will notify you when registration is open for this class!';

  const confirmMsg =
    config?.confirmation ||
    'You\u2019re on the list! We\u2019ll notify you by email when a spot opens up.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'submitWaitlist');
      await fn({
        classCardId: cardId,
        className: cardTitle || '',
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        smsConsent: !!(phone.trim() && smsConsent),
      });
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Waitlist submission error:', err);
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <p style={styles.emptyTitle}>
            {config?.title || 'Join the Waitlist'}
          </p>
          <p style={styles.emptyText}>{confirmMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.emptyState}>
        <p style={styles.emptyTitle}>{headerTitle}</p>
        <p style={styles.emptyText}>{headerMsg}</p>
      </div>
      <form onSubmit={handleSubmit} style={wlStyles.form}>
        <div style={wlStyles.row}>
          {firstNameField.enabled && (
            <div style={wlStyles.field}>
              <label style={wlStyles.label}>{firstNameField.label} *</label>
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={wlStyles.input}
              />
            </div>
          )}
          {lastNameField.enabled && (
            <div style={wlStyles.field}>
              <label style={wlStyles.label}>{lastNameField.label}</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={wlStyles.input}
              />
            </div>
          )}
        </div>
        {emailField.enabled && (
          <div style={wlStyles.field}>
            <label style={wlStyles.label}>{emailField.label} *</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={wlStyles.input}
            />
          </div>
        )}
        {phoneField.enabled && (
          <div style={wlStyles.field}>
            <label style={wlStyles.label}>{phoneField.label}</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={wlStyles.input}
            />
          </div>
        )}
        {phoneField.enabled && phone.trim() && (
          <label style={wlStyles.consent}>
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              style={wlStyles.consentCheckbox}
            />
            <span style={wlStyles.consentText}>
              Notify me by SMS if a spot opens up for this class. Msg &amp; data
              rates may apply. Msg frequency varies. Reply STOP to opt out, HELP
              for help.
            </span>
          </label>
        )}
        {error && <p style={wlStyles.error}>{error}</p>}
        <button
          type="submit"
          disabled={submitting || !firstName.trim() || !email.trim()}
          style={{
            ...styles.confirmBtn,
            background: accent,
            marginTop: 8,
            opacity: submitting || !firstName.trim() || !email.trim() ? 0.6 : 1,
          }}
        >
          {submitting ? 'Joining...' : 'Join Waitlist'}
        </button>
      </form>
    </div>
  );
}

const wlStyles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    maxWidth: 420,
    margin: '0 auto',
  },
  row: {
    display: 'flex',
    gap: 12,
  },
  field: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.7rem',
    color: '#9C7A3C',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  input: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.88rem',
    color: '#1a1208',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #DDD8CC',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
  },
  error: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.8rem',
    color: '#B55A3A',
    fontWeight: 600,
    margin: 0,
  },
  consent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    cursor: 'pointer',
  },
  consentCheckbox: {
    marginTop: 3,
    accentColor: '#9C7A3C',
    cursor: 'pointer',
    flexShrink: 0,
  },
  consentText: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.72rem',
    color: '#5a4a30',
    lineHeight: 1.45,
  },
};

/* ── Main SessionPicker ────────────────────────────────────────────────── */

export default function SessionPicker({
  cardId,
  accent,
  card,
  onSelect,
  onInquire: _onInquire,
  onWaitlist,
}) {
  const { sessions, soldOut, loading, error } = useSessions(cardId);
  const [selectedId, setSelectedId] = useState(null);
  const [seats, setSeats] = useState(1);

  const selected = sessions.find((s) => s.id === selectedId);
  const maxSeats = selected ? selected.spotsRemaining : 1;

  const handleConfirm = () => {
    if (!selected || seats < 1) return;
    onSelect({ session: selected, seats });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <p style={styles.loadingText}>Loading available sessions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <p style={styles.errorText}>
          Unable to load sessions. Please try again later.
        </p>
      </div>
    );
  }

  if (sessions.length === 0) {
    onWaitlist?.();
    return (
      <WaitlistForm
        cardId={cardId}
        cardTitle={card?.title}
        accent={accent}
        reason={soldOut ? 'sold-out' : 'no-sessions'}
      />
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>Select a Session</div>

      {/* Session cards */}
      <div style={styles.sessionList}>
        {sessions.map((s) => {
          const isSelected = selectedId === s.id;
          const isFull = s.spotsRemaining <= 0;

          // Format date for display
          const dateObj = new Date(s.date + 'T12:00:00');
          const dayName = dateObj.toLocaleDateString('en-US', {
            weekday: 'long',
          });
          const monthDay = dateObj.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
          });

          const durationLabel = formatDuration(s.duration);
          const classId = TEMPLATE_TO_CLASS_ICON[card?.templateId] || 1;

          return (
            <motion.button
              key={s.id}
              disabled={isFull}
              onClick={() => {
                setSelectedId(s.id);
                setSeats(1);
              }}
              style={{
                ...styles.sessionCard,
                borderTop: 'none',
                borderRight: 'none',
                borderBottom: 'none',
                borderLeft: `8px solid ${isFull ? '#C9BFA6' : accent}`,
                background: isFull
                  ? '#EEEAE0'
                  : isSelected
                    ? `${accent}18`
                    : '#F9F2E9',
                outline: isSelected ? `2px solid ${accent}` : 'none',
                opacity: 1,
                cursor: isFull ? 'not-allowed' : 'pointer',
              }}
              {...(!isFull && !isSelected
                ? {
                    animate: {
                      scale: [1, 1.015, 1],
                      boxShadow: [
                        '0 2px 8px rgba(26,18,8,0.06)',
                        `0 6px 20px ${accent}22`,
                        '0 2px 8px rgba(26,18,8,0.06)',
                      ],
                    },
                    transition: {
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: sessions.indexOf(s) * 0.4,
                    },
                  }
                : {})}
            >
              {/* Icon + content */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={styles.sessionIcon}>
                  <ClassIcon
                    classId={classId}
                    color={isFull ? '#B0A48A' : accent}
                    size={26}
                  />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Row 1: date left · spots bubble right */}
                  <div style={styles.sessionTop}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'baseline',
                      }}
                    >
                      <div style={styles.sessionDay}>{dayName},</div>
                      <div style={styles.sessionDay}>{monthDay}</div>
                    </div>
                    <span
                      style={{
                        ...styles.pill,
                        background: isFull ? '#B55A3A' : '#E8F5EF',
                        color: isFull ? '#FFFFFF' : '#4A7A5C',
                        letterSpacing: isFull ? '0.1em' : '0.05em',
                        textTransform: isFull ? 'uppercase' : 'none',
                      }}
                    >
                      {isFull
                        ? 'Sold Out'
                        : `${s.spotsRemaining} Spot${s.spotsRemaining === 1 ? '' : 's'} Left`}
                    </span>
                  </div>

                  {/* Row 2: Duration left · Price right */}
                  <div style={{ ...styles.sessionTop, marginTop: 4 }}>
                    {durationLabel && (
                      <div style={styles.sessionMeta}>
                        Duration {durationLabel}
                      </div>
                    )}
                    {s.price != null && (
                      <div
                        style={{
                          ...styles.sessionMeta,
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          paddingRight: '10px',
                        }}
                      >
                        ${s.price}
                      </div>
                    )}
                  </div>

                  {/* Row 3: time left · status bubble right */}
                  <div style={{ ...styles.sessionTop, marginTop: 6 }}>
                    <div style={styles.sessionTime}>
                      {formatTime(s.startTime)} — {formatTime(s.endTime)}
                    </div>
                    <span
                      style={{
                        ...styles.pill,
                        background: isFull ? '#FDE8E8' : '#E8F5EF',
                        color: isFull ? '#B55A3A' : '#4A7A5C',
                      }}
                    >
                      {isFull ? 'Sold Out' : 'Open'}
                    </span>
                  </div>

                  {isSelected && (
                    <div style={{ marginTop: 6 }}>
                      <span
                        style={{ ...styles.selectedBadge, background: accent }}
                      >
                        Selected
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Seat selector — shown after selecting a session */}
      {selected && (
        <div style={styles.seatSection}>
          <div style={styles.seatHeader}>Number of Seats</div>
          <div style={styles.seatRow}>
            <button
              onClick={() => setSeats((s) => Math.max(1, s - 1))}
              disabled={seats <= 1}
              style={styles.seatBtn}
            >
              −
            </button>
            <span style={styles.seatCount}>{seats}</span>
            <button
              onClick={() => setSeats((s) => Math.min(maxSeats, s + 1))}
              disabled={seats >= maxSeats}
              style={styles.seatBtn}
            >
              +
            </button>
          </div>
          <p style={styles.seatNote}>
            {maxSeats} spot{maxSeats === 1 ? '' : 's'} available for this
            session
          </p>

          {/* Confirm button */}
          <button
            onClick={handleConfirm}
            style={{ ...styles.confirmBtn, background: accent }}
          >
            Continue to Payment
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '36px clamp(20px, 5vw, 40px)',
  },
  header: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#9C7A3C',
    marginBottom: 20,
  },
  loadingText: {
    fontFamily: "'Raleway', sans-serif",
    color: '#9C7A3C',
    fontSize: '0.9rem',
    textAlign: 'center',
    padding: '40px 0',
  },
  errorText: {
    fontFamily: "'Raleway', sans-serif",
    color: '#B55A3A',
    fontSize: '0.9rem',
    textAlign: 'center',
    padding: '40px 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 0',
  },
  emptyTitle: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '1rem',
    color: '#1a1208',
    marginBottom: 8,
  },
  emptyText: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.85rem',
    color: '#9C7A3C',
  },
  sessionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 24,
  },
  sessionCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '16px 20px',
    borderRadius: 14,
    textAlign: 'left',
    transition: 'background 0.2s',
    width: '100%',
    fontFamily: "'Raleway', sans-serif",
    boxShadow: '0 1px 6px rgba(26,18,8,0.05)',
  },
  sessionIcon: {
    flexShrink: 0,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sessionTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  sessionDay: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#1a1208',
  },
  sessionDate: {
    fontSize: '0.8rem',
    color: '#9C7A3C',
    marginTop: 2,
  },
  sessionTime: {
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#1a1208',
  },
  sessionMeta: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.78rem',
    color: '#9C7A3C',
    marginTop: 2,
  },
  sessionBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pill: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.05em',
    borderRadius: 50,
    padding: '3px 10px',
    whiteSpace: 'nowrap',
  },
  spotsTag: {
    fontWeight: 700,
    fontSize: '0.75rem',
  },
  selectedBadge: {
    color: '#fff',
    borderRadius: 50,
    padding: '3px 12px',
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  seatSection: {
    borderTop: '1px solid #DDD8CC',
    paddingTop: 24,
  },
  seatHeader: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#9C7A3C',
    marginBottom: 16,
  },
  seatRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    justifyContent: 'center',
    marginBottom: 12,
  },
  seatBtn: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: '1.5px solid #DDD8CC',
    background: '#fff',
    fontSize: '1.2rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Raleway', sans-serif",
    color: '#1a1208',
  },
  seatCount: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '1.5rem',
    color: '#1a1208',
    minWidth: 40,
    textAlign: 'center',
  },
  seatNote: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.75rem',
    color: '#9C7A3C',
    textAlign: 'center',
    marginBottom: 24,
  },
  confirmBtn: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '1rem',
    color: '#fff',
    border: 'none',
    borderRadius: 50,
    padding: '14px 32px',
    cursor: 'pointer',
    width: '100%',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
};
